import { motion } from "framer-motion";
import { Award, Sparkles, ShieldCheck, KeyRound } from "lucide-react";

const pillars = [
  {
    icon: Award,
    title: "Owner-side Advisory",
    body: "We speak only for the property owner. No brokerage splits, no operator kickbacks — a single, transparent fee.",
  },
  {
    icon: Sparkles,
    title: "Live Market Data",
    body: "Every blueprint is benchmarked against live ADR, RevPAR, and stay-length data from your exact micro-market.",
  },
  {
    icon: ShieldCheck,
    title: "Never Custody Capital",
    body: "Rent, deposits, and vendor payments flow through your accounts — never through ours. Independence by structure.",
  },
  {
    icon: KeyRound,
    title: "One Calm Mandate",
    body: "Design, operations and yield folded into a single written mandate — never the fragmented vendor stack most owners inherit.",
  },
];

export default function About() {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="bg-[#F7F3EA] py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-5"
          >
            <div className="text-xs tracking-[0.3em] uppercase text-[#C5A059] mb-5">
              About Kenneth Nathanael Lukman LLC
            </div>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-[#2B3324] leading-[1.1]">
              Independent counsel for{" "}
              <span className="italic text-[#6E7F5B]">prime property owners</span>
              .
            </h2>
            <div className="gold-accent mt-6" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-7 space-y-6 text-[#5C6B4D] text-base md:text-lg leading-relaxed font-light"
          >
            <p>
              Kenneth Nathanael Lukman LLC is a boutique property consultancy
              serving private owners, family offices and investors across the
              United Kingdom — with our deepest bench in central London, from
              the creative pulse of Soho and the cobbled elegance of Covent
              Garden to the storied grandeur of Trafalgar Square.
            </p>
            <p>
              We curate and oversee premium serviced apartments on behalf of
              our clients, folding design, operations and yield strategy into a
              single, calm mandate. Every asset under our watch is positioned,
              refurbished and stewarded to the discipline of a five-star
              practice — always in the owner&apos;s name, always with the owner
              in control.
            </p>
            <p className="text-[#2B3324]">
              We exist for property owners who want the standard of a
              professional operator without the burden of running one.
            </p>
          </motion.div>
        </div>

        <div className="mt-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {pillars.map((p, i) => (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              data-testid={`pillar-${i}`}
              className="bg-white p-7 rounded-lg border border-[#E5E0D5]/70 shadow-[0_4px_20px_rgb(0,0,0,0.03)] hover:shadow-[0_12px_36px_rgb(0,0,0,0.08)] transition-all duration-500 hover:-translate-y-1"
            >
              <p.icon className="w-6 h-6 text-[#C5A059]" strokeWidth={1.5} />
              <h3 className="font-display text-lg font-medium tracking-tight text-[#2B3324] mt-5">
                {p.title}
              </h3>
              <p className="mt-3 text-sm text-[#5C6B4D] leading-relaxed">
                {p.body}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
